/**
 * Copied from slate-md-serializer and draft-js-export-markdown so that the editor can serialize and deserialize properly
 */
import { Value } from 'slate'

import parser from './parser'

const encodeUrl = (href: any) => {
  return decodeSafeUrl(href)
    .trim()
    .replaceAll(' ', '%20')
    .replaceAll("'", '%27')
    .replaceAll('(', '%28')
    .replaceAll(')', '%29')
}

const decodeSafeUrl = (uri: any) => {
  const components = uri.split(/(%(?:d0|d1)%.{2})/)
  return components
    .map((component: any) => {
      try {
        return decodeURIComponent(component)
      } catch {
        return component.replaceAll(/%(?!\d+)/g, '%25')
      }
    })
    .join('')
}

const ZERO_WIDTH_SPACE = '\u200B'

const RULES = [
  {
    name: 'string',
    serialize(obj: any, children: any) {
      if (obj.object === 'string') {
        return children
      }
    }
  },
  {
    name: 'block',
    serialize(obj: any, children: any, doc?: any, vars?: any) {
      if (obj.object !== 'block') return
      const parent = doc.getParent(obj.key)

      switch (obj.type) {
        case 'table': {
          vars.tableHeader = ''

          // trim removes trailing newline
          return children.trim()
        }
        case 'table-head': {
          switch (obj.getIn(['data', 'align'])) {
            case 'left': {
              vars.tableHeader += '|:--- '
              break
            }
            case 'center': {
              vars.tableHeader += '|:---:'
              break
            }
            case 'right': {
              vars.tableHeader += '| ---:'
              break
            }
            default: {
              vars.tableHeader += '| --- '
            }
          }
          return `| ${children} `
        }
        case 'table-row': {
          let output = ''
          if (vars.tableHeader) {
            output = `${vars.tableHeader}|\n`
            vars.tableHeader = ''
          }
          return `${children}|\n${output}`
        }
        case 'table-cell': {
          return `| ${children} `
        }
        case 'paragraph': {
          return children
        }
        case 'code': {
          const language = obj.getIn(['data', 'language']) || ''
          return `\`\`\`${language}\n${children}\n\`\`\``
        }
        case 'code-line': {
          return `${children}\n`
        }
        case 'block-quote': {
          // Handle multi-line blockquotes
          return children
            .split('\n')
            .map((text: any) => `> ${text}`)
            .join('\n')
        }
        case 'numbered-list':
        case 'todo-list':
        case 'bulleted-list':
        case 'ordered-list': {
          // root list
          if (parent === doc) {
            return children
          }

          // nested list
          return `\n${children.replaceAll(/\n+$/gm, '').replaceAll(/^/gm, '   ')}`
        }
        case 'list-item': {
          switch (parent.type) {
            case 'numbered-list':
            case 'ordered-list': {
              return `1. ${children}\n`
            }
            case 'todo-list': {
              const checked = obj.getIn(['data', 'checked'])
              const box = checked ? '[x]' : '[ ]'
              return `${box} ${children}\n`
            }
            default:
            case 'bulleted-list': {
              return `* ${children}\n`
            }
          }
        }
        case 'heading1': {
          return `# ${children}\n`
        }
        case 'heading2': {
          return `\n## ${children}\n`
        }
        case 'heading3': {
          return `\n### ${children}\n`
        }
        case 'heading4': {
          return `\n#### ${children}\n`
        }
        case 'heading5': {
          return `\n##### ${children}\n`
        }
        case 'heading6': {
          return `\n###### ${children}\n`
        }
        case 'horizontal-rule': {
          return `---`
        }

        case 'image': {
          const alt = obj.getIn(['data', 'alt']) || ''
          const src = encodeUrl(obj.getIn(['data', 'src']) || '')
          return `![${alt}](${src})`
        }
        default: {
          return ''
        }
      }
    }
  },
  {
    name: 'hashtag',
    serialize(obj: any, children: any) {
      if (obj.type === 'hashtag') return children
    }
  },
  {
    name: 'link',
    serialize(obj: any, children: any) {
      if (obj.type === 'link') {
        const href = obj.getIn(['data', 'href']) || ''
        const text = children.trim() || href
        return href ? `[${text}](${href})` : text
      }
    }
  },
  {
    name: 'mark',
    serialize(obj: any, children: any) {
      if (obj.object !== 'mark') return
      if (!children) return

      const match = children.match(/\s+$/)
      const spacesBefore = children.search(/\S|$/)
      const spacesAfter = match ? match[0].length : 0
      const sB = Array.from({ length: spacesBefore + 1 }).join(' ')
      const sA = Array.from({ length: spacesAfter + 1 }).join(' ')
      const content = children.trim()

      switch (obj.type) {
        case 'bold': {
          return `${sB}**${content}**${sA}`
        }
        case 'italic': {
          return `${sB}_${content}_${sA}`
        }
        case 'code': {
          return `${sB}\`${content}\`${sA}`
        }
        case 'inserted': {
          return `${sB}++${content}++${sA}`
        }
        case 'deleted': {
          return `${sB}~~${content}~~${sA}`
        }
        case 'underlined': {
          return `${sB}__${content}__${sA}`
        }
        default: {
          return ''
        }
      }
    }
  }
]

const serializeNode = (node: any, doc: any, vars?: any) => {
  if (node.object === 'text') {
    const leaves = node.getLeaves()
    return leaves.map((leave: any) => {
      return serializeLeaves(leave)
    })
  }

  const children = node.nodes
    .map((childNode: any) => {
      const serialized = serializeNode(childNode, doc)
      return (serialized && serialized.join ? serialized.join('') : serialized) || ''
    })
    .join(
      // Special case for blockquotes, children in blockquotes are separated by new lines
      node.type === 'block-quote' ? '\n' : ''
    )

  for (const rule of RULES) {
    if (!rule.serialize) continue
    const ret = rule.serialize(node, children, doc, vars)
    if (ret) return ret
  }
}

const serializeLeaves = (leaves: any) => {
  const text = serializeString(leaves.text)
  return leaves.marks.reduce((children: any, mark: any) => {
    for (const rule of RULES) {
      if (!rule.serialize) continue
      const ret = rule.serialize(mark, children)
      if (ret) return ret
    }
    return ''
  }, text)
}

const serializeString = (string: any) => {
  for (const rule of RULES) {
    if (!rule.serialize) continue
    const ret = rule.serialize({ object: 'string', type: 'string' }, string)
    if (ret) return ret
  }
}

export function serialize(value: any) {
  const doc = value.document

  //to ensure there are proper linebreaks add the zero width space
  const nodes = doc.nodes
    .toArray()
    .map((node: any) => serializeNode(node, doc, { tableHeader: '' }) || ZERO_WIDTH_SPACE)

  const op = nodes.join('\n\n')

  return op
    .replaceAll(/\r+/g, '')
    .replaceAll(/^\s+/g, '')
    .replaceAll('****', '')
    .replaceAll('++++', '')
    .replaceAll('~~~~', '')
    .replaceAll('____', '')
    .replaceAll('``\\', '\\')
    .replaceAll('__\\', '\\')
    .replaceAll('\r', '')
    .trim()
}

export function deserialize(value: any) {
  const document = parser.parse(value)
  return Value.fromJSON({ document })
}
